import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, User as UserIcon, Chrome } from 'lucide-react';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('buyer'); // buyer or dealer
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name, role);
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-20">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-12 h-12 bg-primary rounded-sm flex items-center justify-center">
              <span className="text-3xl font-bold font-heading">J</span>
            </div>
          </div>
          <h1 className="text-3xl font-heading font-bold mb-2" data-testid="auth-title">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p className="text-textSecondary">
            {isLogin ? 'Sign in to start bidding' : 'Join JAMOH & CO today'}
          </p>
        </div>

        <div className="glass p-8 rounded-sm">
          {/* Google Login */}
          <button
            onClick={loginWithGoogle}
            data-testid="google-login-button"
            className="w-full bg-white text-black hover:bg-gray-100 rounded-sm font-bold py-3 mb-6 flex items-center justify-center space-x-2 transition-colors"
          >
            <Chrome className="w-5 h-5" />
            <span>Continue with Google</span>
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-surface text-textSecondary">Or continue with email</span>
            </div>
          </div>

          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-500 px-4 py-3 rounded-sm mb-6" data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">Full Name</label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textSecondary" />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required={!isLogin}
                      data-testid="name-input"
                      className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm pl-12 pr-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary placeholder:text-white/30"
                      placeholder="John Doe"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-3">Account Type</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setRole('buyer')}
                      className={`p-4 rounded-sm border-2 transition-all ${
                        role === 'buyer'
                          ? 'border-primary bg-primary/10'
                          : 'border-white/10 bg-surfaceHighlight hover:border-white/20'
                      }`}
                    >
                      <div className="text-center">
                        <UserIcon className="w-6 h-6 mx-auto mb-2" />
                        <div className="font-heading font-bold">Buyer</div>
                        <div className="text-xs text-textSecondary mt-1">Browse and bid on vehicles</div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRole('dealer')}
                      className={`p-4 rounded-sm border-2 transition-all ${
                        role === 'dealer'
                          ? 'border-primary bg-primary/10'
                          : 'border-white/10 bg-surfaceHighlight hover:border-white/20'
                      }`}
                    >
                      <div className="text-center">
                        <Chrome className="w-6 h-6 mx-auto mb-2" />
                        <div className="font-heading font-bold">Dealer</div>
                        <div className="text-xs text-textSecondary mt-1">List vehicles for sale</div>
                      </div>
                    </button>
                  </div>
                  {role === 'dealer' && (
                    <div className="mt-3 p-3 bg-yellow-600/10 border border-yellow-600/30 rounded-sm text-sm text-textSecondary">
                      <strong className="text-yellow-500">Note:</strong> Dealer accounts require admin approval before you can list vehicles.
                    </div>
                  )}
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textSecondary" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                  className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm pl-12 pr-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary placeholder:text-white/30"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textSecondary" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                  className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm pl-12 pr-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary placeholder:text-white/30"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="submit-auth-button"
              className="w-full bg-primary hover:bg-primaryHover text-white rounded-sm font-bold tracking-wide uppercase py-3 transition-all duration-300 red-glow disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
            </button>

            {isLogin && (
              <div className="text-right mt-2">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-primary hover:underline">
                  Forgot Password?
                </Link>
              </div>
            )}
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-textSecondary">
              {isLogin ? "Don't have an account?" : "Already have an account?"}
            </span>
            {' '}
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
              }}
              data-testid="toggle-auth-mode"
              className="text-primary hover:underline font-medium"
            >
              {isLogin ? 'Sign Up' : 'Sign In'}
            </button>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-textSecondary hover:text-primary transition-colors">
            ← Back to Homepage
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;